import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Wrench } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = [
  { value: "welding_rod", label: "Welding Rod" },
  { value: "abrasive", label: "Abrasive" },
  { value: "cement", label: "Cement" },
  { value: "diesel", label: "Diesel" },
  { value: "other", label: "Other" },
];

const UNITS = [
  { value: "box", label: "Box" },
  { value: "bag", label: "Bag" },
  { value: "litre", label: "Litre" },
  { value: "piece", label: "Piece" },
  { value: "kg", label: "Kg" },
  { value: "meter", label: "Meter" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export default function IndustrialItems() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", brand: "", unit: "box", category: "welding_rod" });
  const [filterCat, setFilterCat] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/industrial/items");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post("/industrial/items", form);
      setForm({ name: "", brand: "", unit: form.unit, category: form.category });
      load();
      toast.success("Item added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/industrial/items/${id}`);
      load();
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = filterCat === "all" ? items : items.filter((i) => i.category === filterCat);

  return (
    <div className="space-y-4" data-testid="industrial-items-page">
      <p className="small-label text-gray-500">
        Master list of non-LPG products you buy &amp; sell (welding rods, abrasives, cement, diesel, etc.). Add items here first; then use Industrial Purchase / Sale to record transactions.
      </p>

      {isAdmin && (
        <form onSubmit={add} className="card-flat p-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="industrial-item-form">
          <div>
            <label className="small-label text-gray-500">Item Name *</label>
            <input className="input-lg w-full" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Welding Rod 3.15mm" data-testid="ind-item-name" />
          </div>
          <div>
            <label className="small-label text-gray-500">Brand</label>
            <input className="input-lg w-full" value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder="e.g. Ador, ESAB" data-testid="ind-item-brand" />
          </div>
          <div>
            <label className="small-label text-gray-500">Unit</label>
            <select className="input-lg w-full" value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              data-testid="ind-item-unit">
              {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label text-gray-500">Category</label>
            <select className="input-lg w-full" value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="ind-item-category">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button className="btn-primary flex items-center gap-1 w-full justify-center" data-testid="ind-item-add-btn">
              <Plus size={14} /> Add Item
            </button>
          </div>
        </form>
      )}

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <Wrench size={18} />
            <div className="font-heading font-bold text-lg">Items ({filtered.length})</div>
          </div>
          <select className="input-lg" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="ind-item-filter-cat">
            <option value="all">All Categories</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left small-label text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Brand</th>
                <th className="px-4 py-2">Unit</th>
                <th className="px-4 py-2">Category</th>
                {isAdmin && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((it) => (
                <tr key={it.id} data-testid={`ind-item-row-${it.id}`}>
                  <td className="px-4 py-2 font-semibold">{it.name}</td>
                  <td className="px-4 py-2">{it.brand || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-2 uppercase tracking-wide text-xs">{it.unit}</td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-0.5 rounded-sm bg-gray-100 text-xs font-semibold">
                      {CATEGORY_LABEL[it.category] || it.category}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2 text-right">
                      <button className="text-red-600 hover:text-red-800" onClick={() => del(it.id)}
                        data-testid={`ind-item-del-${it.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-gray-400 text-sm">No items yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
